import React, { useMemo } from 'react';
import { Clock, Check, X, Shield } from 'lucide-react';
import { calculateDaysBetween, getLeaveYear, getUsedDaysByLeaveYear, LEAVE_TYPES } from '../utils/vacationUtils';

const getBalanceForYear = (userId, annualBalances, year) => {
  if (!annualBalances || !annualBalances.length) return 0;
  const yearKey = `balance_${year}`;
  const pivoted = annualBalances.find(b => Number(b.userId || b.user_id) === Number(userId));
  if (pivoted && pivoted[yearKey] !== undefined) return Number(pivoted[yearKey]);
  const raw = annualBalances.find(b =>
    Number(b.userId || b.user_id) === Number(userId) &&
    Number(b.cycle_start_year || b.year) === year
  );
  return raw?.total_allowed !== undefined ? Number(raw.total_allowed) : 0;
};

const RequestsView = ({
  currentUser,
  vacationRequests,
  userDatabase,
  departments,
  holidays = [],
  annualBalances = [],
  selectedLeaveYears = [],
  onYearChange,
}) => {
  const currentLeaveYear = getLeaveYear(new Date());

  const displayYears = useMemo(() => {
    const years = new Set();

    annualBalances.forEach(b => {
      if (Number(b.userId || b.user_id) !== Number(currentUser.id)) return;
      Object.keys(b).forEach(key => {
        const m = key.match(/^balance_(\d{4})$/);
        if (m) years.add(Number(m[1]));
      });
      const y = b.cycle_start_year || b.year;
      if (y) years.add(Number(y));
    });

    vacationRequests
      .filter(req => (req.user_id || req.userId) === currentUser.id)
      .forEach(req => {
        const y = getLeaveYear(req.start_date || req.startDate);
        if (y) years.add(y);
      });

    return Array.from(years).sort((a, b) => a - b);
  }, [annualBalances, vacationRequests, currentUser.id]);

  const toggleYear = (year) => {
    if (selectedLeaveYears.includes(year)) {
      if (selectedLeaveYears.length === 1) return;
      onYearChange(selectedLeaveYears.filter(y => y !== year));
    } else {
      onYearChange([...selectedLeaveYears, year].sort((a, b) => a - b));
    }
  };

  const activeYears = useMemo(() => {
    return selectedLeaveYears.length > 0
      ? selectedLeaveYears
      : [currentLeaveYear];
  }, [selectedLeaveYears, currentLeaveYear]);

  const filteredRequests = useMemo(() => {
    return vacationRequests
      .filter(req => {
        if ((req.user_id || req.userId) !== currentUser.id) return false;
        return activeYears.includes(getLeaveYear(req.start_date || req.startDate));
      })
      .sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
  }, [vacationRequests, currentUser.id, activeYears]);

  const getStatusInfo = (request) => {
    switch (request.status) {
      case 'pending':
        return { icon: <Clock className="h-4 w-4" />, color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800', label: 'Αναμονή Έγκρισης' };
      case 'manager1_approved':
        return { icon: <Shield className="h-4 w-4" />, color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800', label: 'Εγκρίθηκε από Δ1 - Αναμονή CEO' };
      case 'approved':
        return { icon: <Check className="h-4 w-4" />, color: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800', label: 'Εγκρίθηκε' };
      default:
        return { icon: <X className="h-4 w-4" />, color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800', label: 'Απορρίφθηκε' };
    }
  };

  const getManagerName = (managerId) => {
    if (!managerId) return 'Άγνωστος';
    return userDatabase.find(u => u.id === managerId)?.name || 'Άγνωστος';
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-jewel-text mb-3">Ανάλυση Αδειών ανά Τύπο</h2>
        <div className="space-y-3">
          {activeYears.map(year => {
            const total      = getBalanceForYear(currentUser.id, annualBalances, year);
            const rawUsed    = getUsedDaysByLeaveYear(currentUser.id, vacationRequests, year, holidays);
            const used       = total > 0 ? Math.min(rawUsed, total) : rawUsed;
            const overflow   = total > 0 ? Math.max(0, rawUsed - total) : 0;

            const prevTotal  = getBalanceForYear(currentUser.id, annualBalances, year - 1);
            const prevRawUsed = getUsedDaysByLeaveYear(currentUser.id, vacationRequests, year - 1, holidays);
            const overflowFromPrev = prevTotal > 0 ? Math.max(0, prevRawUsed - prevTotal) : 0;

            const remaining  = Math.max(0, total - used - overflowFromPrev);

            const cycleRequests = vacationRequests.filter(req =>
              (req.user_id || req.userId) === currentUser.id &&
              getLeaveYear(req.start_date || req.startDate) === year &&
              (req.status === 'approved' || req.status === 'pending' || req.status === 'manager1_approved') &&
              ['vacation', 'mandatory'].includes((req.leave_type || req.leaveType || '').toLowerCase())
            );

            return (
              <div key={year} className="border border-gray-200 dark:border-jewel-border rounded-lg overflow-hidden">
                <div className="bg-gray-50 dark:bg-jewel-bg px-4 py-2 border-b border-gray-200 dark:border-jewel-border">
                  <p className="text-xs font-bold text-gray-500 dark:text-jewel-text-muted uppercase tracking-wide">
                    1 Απριλίου {year} – 31 Μαρτίου {year + 1}
                  </p>
                </div>
                <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-jewel-border bg-white dark:bg-jewel-bg px-4 py-3">
                  <div className="text-center pr-4">
                    <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold mb-1">Σύνολο</p>
                    <p className="text-lg font-bold text-gray-800 dark:text-jewel-text">{total > 0 ? total : '—'}</p>
                  </div>
                  <div className="text-center px-4">
                    <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold mb-1">Εγκρίθηκαν</p>
                    <p className="text-lg font-bold text-blue-600 dark:text-jewel-teal-tint">{used}</p>
                    {overflow > 0 && (
                      <p className="text-[10px] text-orange-500 dark:text-orange-400 font-bold">+{overflow} υπέρβαση</p>
                    )}
                  </div>
                  <div className="text-center pl-4">
                    <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold mb-1">Απομένουν</p>
                    <p className={`text-lg font-bold ${remaining <= 5 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                      {total > 0 ? remaining : 0}
                    </p>
                  </div>
                </div>
                {overflow > 0 && (
                  <div className="px-4 py-2 bg-orange-50 dark:bg-orange-900/30 border-t border-orange-100 dark:border-orange-800">
                    <p className="text-xs text-orange-700 dark:text-orange-400">
                      ⚠️ {overflow} ημέρες χρησιμοποιήθηκαν από το επόμενο έτος ({year + 1}/{year + 2})
                    </p>
                  </div>
                )}
                {overflowFromPrev > 0 && (
                  <div className="px-4 py-2 bg-red-50 dark:bg-red-900/30 border-t border-red-100 dark:border-red-800">
                    <p className="text-xs text-red-700 dark:text-red-400">
                      ⚠️ {overflowFromPrev} ημέρες χρεώθηκαν από το προηγούμενο έτος ({year - 1}/{year})
                    </p>
                  </div>
                )}
                {cycleRequests.length === 0 ? (
                  <div className="px-4 py-2 bg-white dark:bg-jewel-bg border-t border-gray-100 dark:border-jewel-border">
                    <p className="text-xs text-gray-400 dark:text-jewel-text-muted italic">Καμία άδεια αυτήν την περίοδο</p>
                  </div>
                ) : (
                  <div className="bg-white dark:bg-jewel-bg border-t border-gray-100 dark:border-jewel-border divide-y divide-gray-50 dark:divide-jewel-border">
                    {cycleRequests.map(req => {
                      const lt   = LEAVE_TYPES[req.leave_type || req.leaveType] || LEAVE_TYPES.vacation;
                      const days = calculateDaysBetween(req.start_date || req.startDate, req.end_date || req.endDate, holidays);
                      const start = new Date(req.start_date || req.startDate);
                      const end   = new Date(req.end_date   || req.endDate);
                      const si    = getStatusInfo(req);
                      return (
                        <div key={req.id} className="px-4 py-2 flex items-center justify-between text-xs gap-4">
                          <div className="flex items-center gap-2 min-w-0">
                            <span>{lt.icon}</span>
                            <span className="text-gray-700 dark:text-jewel-text font-medium truncate">
                              {start.toLocaleDateString('el-GR')} – {end.toLocaleDateString('el-GR')}
                            </span>
                            <span className="text-gray-400 dark:text-jewel-text-muted">({days} ημ.)</span>
                          </div>
                          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border whitespace-nowrap ${si.color}`}>
                            {si.icon}
                            <span className="font-semibold">{si.label}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {displayYears.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-jewel-text-muted italic">Δεν υπάρχουν δεδομένα υπολοίπων.</p>
          )}
        </div>
      </div>
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-jewel-text">Οι Αιτήσεις μου</h2>
          {displayYears.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-jewel-text-muted font-medium">Χρονολογία:</span>
              {displayYears.map(year => (
                <button
                  key={year}
                  onClick={() => toggleYear(year)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    activeYears.includes(year)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-jewel-bg text-gray-600 dark:text-jewel-text border-gray-300 dark:border-jewel-border hover:border-blue-400'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-400 dark:text-jewel-text-muted mb-4">(1 Απριλίου – 31 Μαρτίου)</p>
        {filteredRequests.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 dark:bg-jewel-bg rounded-lg border-2 border-dashed border-gray-300 dark:border-jewel-border">
            <Clock className="h-10 w-10 mx-auto mb-2 text-gray-300 dark:text-jewel-text-muted" />
            <p className="text-gray-400 dark:text-jewel-text-muted text-sm">
              Δεν υπάρχουν αιτήσεις για {activeYears.length === 1
                ? `το ${activeYears[0]}/${activeYears[0] + 1}`
                : `τα επιλεγμένα έτη`}.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredRequests.map(request => {
              const statusInfo    = getStatusInfo(request);
              const leaveTypeInfo = LEAVE_TYPES[request.leave_type || request.leaveType] || LEAVE_TYPES.vacation;
              const startDate     = new Date(request.start_date || request.startDate);
              const endDate       = new Date(request.end_date   || request.endDate);
              const workingDays   = calculateDaysBetween(request.start_date || request.startDate, request.end_date || request.endDate, holidays);
              const statusBorderClass = statusInfo.color.split(' ').find(c => c.startsWith('border-') && !c.startsWith('border-2') && !c.includes('dark:'));
              const statusBorderDarkClass = statusInfo.color.split(' ').find(c => c.startsWith('dark:border-'));

              return (
                <div key={request.id} className={`bg-white dark:bg-jewel-bg rounded-lg p-4 ${
                  (request.leave_type || request.leaveType) === 'unpaid' && (request.manager_granted || request.managerGranted)
                  ? 'border-4 border-red-600'
                  : `border-2 ${statusBorderClass || ''} ${statusBorderDarkClass || ''}`
                }`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl">{leaveTypeInfo.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-800 dark:text-jewel-text">{leaveTypeInfo.label}</h3>
                        <p className="text-xs text-gray-500 dark:text-jewel-text-muted">{workingDays} εργάσιμες ημέρες</p>
                      </div>
                    </div>
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full border text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.icon}
                      <span className="ml-1">{statusInfo.label}</span>
                    </div>
                  </div>
                  <div className="mb-3 p-3 bg-white/60 dark:bg-jewel-bg/40 rounded border border-gray-100 dark:border-jewel-border shadow-sm">
                    <p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase font-bold mb-1">Περίοδος Άδειας</p>
                    <p className="text-sm text-gray-700 dark:text-jewel-text">
                      <span className="font-bold text-gray-900 dark:text-jewel-text">
                        {startDate.toLocaleDateString('el-GR', { weekday: 'long' })} {startDate.toLocaleDateString('el-GR')}
                      </span>
                      <span className="mx-2 text-gray-400 dark:text-jewel-text-muted">έως και</span>
                      <span className="font-bold text-gray-900 dark:text-jewel-text">
                        {endDate.toLocaleDateString('el-GR', { weekday: 'long' })} {endDate.toLocaleDateString('el-GR')}
                      </span>
                    </p>
                  </div>
                  {request.reason && (
                    <p className="text-xs text-gray-600 dark:text-jewel-text italic bg-gray-50 dark:bg-jewel-bg/50 p-2 rounded mb-3 border border-gray-100 dark:border-jewel-border">
                      "{request.reason}"
                    </p>
                  )}
                  <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-jewel-border space-y-1">
                    {request.manager_granted || request.managerGranted ? (
                      <div className="flex items-center text-[11px] font-semibold">
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          (request.leave_type || request.leaveType) === 'unpaid' ? 'bg-orange-500' : 'bg-purple-500'
                        }`} />
                        <span className={
                          (request.leave_type || request.leaveType) === 'unpaid' ? 'text-orange-600 dark:text-orange-400' : 'text-purple-600 dark:text-jewel-ruby-tint'
                        }>
                          {(request.leave_type || request.leaveType) === 'unpaid'
                          ? `Χορηγήθηκε (Άνευ Αποδοχών) από: ${getManagerName(request.reviewed_by || request.reviewedBy)}`
                          : `Χορηγήθηκε από: ${getManagerName(request.reviewed_by || request.reviewedBy)}`}
                        </span>
                      </div>
                      ) : (
                        <>
                          <div className="flex items-center text-[11px] text-gray-500 dark:text-jewel-text-muted">
                            <div className={`w-2 h-2 rounded-full mr-2 ${request.manager1_status === 'manager1_approved' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                            Δ1: {request.manager1_id ? getManagerName(request.manager1_id) : 'Εκκρεμεί'}
                          </div>
                          {request.manager1_status === 'manager1_approved' && (
                            <div className="flex items-center text-[11px] text-gray-500 dark:text-jewel-text-muted">
                              <div className={`w-2 h-2 rounded-full mr-2 ${request.status === 'approved' ? 'bg-green-500' : 'bg-blue-500'}`} />
                              Δ2: {request.manager2_id ? getManagerName(request.manager2_id) : 'Αναμονή'}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestsView;